import { useCallback } from 'react';
import {
  AssetLibraryEventRecordType,
  POST_ASSET_LIBRARY_EVENT_NAME,
} from './constants';

/**
 * Event logging used to gather adoption metrics for the copilot asset library suggestions in campaign flow.
 * @param {Function} postAssetEventLog - Logging function.
 * @returns {Object} - An object containing the event logging functions.
 */
export function useAssetLibraryEventLog(postAssetEventLog) {
  const logEnterAssetLibrary = useCallback(() => {
    postAssetEventLog({
      EventName: POST_ASSET_LIBRARY_EVENT_NAME,
      EventValue: {
        RecordType: AssetLibraryEventRecordType.ENTER_ASSET_LIBRARY,
      },
    });
  }, [postAssetEventLog]);

  const logEnterGenerateImagesPanel = useCallback(() => {
    postAssetEventLog({
      EventName: POST_ASSET_LIBRARY_EVENT_NAME,
      EventValue: {
        RecordType: AssetLibraryEventRecordType.ENTER_GENERATE_IMAGES_PANEL,
      },
    });
  }, [postAssetEventLog]);

  const logGenerateImages = useCallback((prompt) => {
    postAssetEventLog({
      EventName: POST_ASSET_LIBRARY_EVENT_NAME,
      EventValue: {
        RecordType: AssetLibraryEventRecordType.GENERATE_IMAGES,
        Prompt: prompt,
      },
    });
  }, [postAssetEventLog]);

  const logReturnImages = useCallback((suggestionIds) => {
    postAssetEventLog({
      EventName: POST_ASSET_LIBRARY_EVENT_NAME,
      EventValue: {
        RecordType: AssetLibraryEventRecordType.RETURN_IMAGES,
        SuggestionIds: suggestionIds,
      },
    });
  }, [postAssetEventLog]);

  const logAddImagesToAssetLibrary = useCallback((suggestionIds, prompt) => {
    postAssetEventLog({
      EventName: POST_ASSET_LIBRARY_EVENT_NAME,
      EventValue: {
        RecordType: AssetLibraryEventRecordType.ADD_IMAGES_TO_ASSET_LIBRARY,
        SuggestionIds: suggestionIds,
        Prompt: prompt,
      },
    });
  }, [postAssetEventLog]);

  return {
    logEnterAssetLibrary,
    logEnterGenerateImagesPanel,
    logGenerateImages,
    logReturnImages,
    logAddImagesToAssetLibrary,
  };
}

export function useAssetLibraryEventHandler(postAssetEventLog) {
  const {
    logEnterAssetLibrary,
    logEnterGenerateImagesPanel,
    logGenerateImages,
    logReturnImages,
    logAddImagesToAssetLibrary,
  } = useAssetLibraryEventLog(postAssetEventLog);

  const onEnterAssetLibrary = useCallback(() => {
    logEnterAssetLibrary();
  }, [logEnterAssetLibrary]);

  const onEnterGenerateImagesPanel = useCallback(() => {
    logEnterGenerateImagesPanel();
  }, [logEnterGenerateImagesPanel]);

  const onGenerateImages = useCallback(({ prompt }) => {
    logGenerateImages(prompt);
  }, [logGenerateImages]);

  const onReturnImages = useCallback(({ imageIds }) => {
    logReturnImages(imageIds);
  }, [logReturnImages]);

  const onAddImagesToAssetLibrary = useCallback(({ imageIds, prompt }) => {
    logAddImagesToAssetLibrary(imageIds, prompt);
  }, [logAddImagesToAssetLibrary]);

  return {
    onAddImagesToAssetLibrary,
    onGenerateImages,
    onReturnImages,
    onEnterGenerateImagesPanel,
    onEnterAssetLibrary,
  };
}
